import _ from 'underscore';
import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Gavel20Regular } from '@fluentui/react-icons';
import { StatusSummaryCard } from '@bingads-webui-campaign-react/status-summary-card';
import { BidStrategyComponent, MAX_CONVERSIONS, MAX_CONVERSIONS_VALUE } from '@bingads-webui-campaign-react/bid-strategy-component';
import { FORM_STATES, Form, useFormService } from '@bingads-webui-react/form';
import { FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { withFooter } from './with-footer';
import { useBidStrategyRepository } from './use-repository';
import { useReadCampaign } from './use-read-campaign';
import { getApiErrorMapper } from '../utils/api-error-mapper';
import { getLinkedInBidStrategyText, getLinkedInBidStrategyOptions } from '../utils/linkedin-campaign-utils';
import { useBidStrategyCardStyles } from '../styles/bid-strategy-card-styles';

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const FormViewWrapper = ({
  data,
  onDataChange,
  error,
}: { data: any; onDataChange: Function; error: any; }) => {
  const errors = error?.error?.errors || [];
  const {
    campaignType,
    campaignGoal,
    bidStrategy,
    cpa,
    roas,
    isInitialAdditionalDetailsChecked,
    currency,
    budgetAmount,
  } = data || {};
  const { state } = useFormService();
  const isLoading = state.matches(FORM_STATES.LOADING);
  const classes = useBidStrategyCardStyles();

  const i18n = useContext(I18nContext) as any;
  const apiErrorMapper = getApiErrorMapper(i18n);

  const serverContext = useContext(ServerContext) as any;
  const bidRange = (serverContext && serverContext.KeywordBidRange) || null;

  const options = useMemo(() => {
    if (campaignType === 'PerformanceMax') {
      return [MAX_CONVERSIONS, MAX_CONVERSIONS_VALUE];
    }
    if (campaignType === 'LinkedIn') {
      return getLinkedInBidStrategyOptions(campaignGoal);
    }
    return [];
  }, [campaignGoal, campaignType]);

  const onFieldChange = useCallback((updatedData) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        ...updatedData,
      }),
    });
  }, [onDataChange]);

  const onBidStrategyChange = useCallback((e, newBidStrategy) => {
    const bidStrategyType = newBidStrategy?.type;
    onFieldChange({ bidStrategy: bidStrategyType });
  }, [onFieldChange]);

  const onMarketingMetricChange = useCallback(({
    data: newMarketingMetric,
    isValid,
    messages,
  }) => {
    onFieldChange({
      ...newMarketingMetric,
      isMarketingMetricValid: isValid,
      marketingMetricErrors: messages,
    });
  }, [onFieldChange]);

  return (
    <div className={classes.container}>
      <FormErrorGroup formErrorProps={{ errors, errorMapper: apiErrorMapper }}>
        <BidStrategyComponent
          options={options}
          selectedBidStrategy={bidStrategy}
          onBidStrategyChange={onBidStrategyChange}
          marketingMetricsValue={{ cpa, roas }}
          onMarketingMetricChange={onMarketingMetricChange}
          defaultChecked={isInitialAdditionalDetailsChecked}
          inputUnit={currency}
          bidRange={bidRange}
          isLoading={isLoading}
          hideMessage
          hideAdditionalDetails={campaignType !== 'PerformanceMax'}
          budgetAmount={budgetAmount}
          scenario="campaign-status-summary-card"
          withTargetCpaWarning
        />

        <UnmappedFormErrors className="server-errors" />
      </FormErrorGroup>
    </div>);
};

const BidStrategyForm = ({ closeDialog, log }: { closeDialog: Function, log: { trace: Function } }) => {
  const onDoneSaving = useCallback((...params) => {
    log.trace('Bid strategy form popover closed: saved');
    closeDialog(...params);
  }, [log, closeDialog]);

  const onCanceled = useCallback((...params) => {
    log.trace('Bid strategy form popover closed: canceled');
    closeDialog(...params);
  }, [log, closeDialog]);

  useEffect(() => {
    log.trace('Bid strategy card popover rendered');
  }, [log]);

  const config = useMemo(() => ({
    id: 'bid-strategy-form',
    view: withFooter(FormViewWrapper),
    repository: useBidStrategyRepository,
    machineOptions: {
      actions: {
        onDoneSaving,
        onCanceled,
      },
    },
  }), [onCanceled, onDoneSaving]);
  return (<Form config={config} />);
};

const getBidStrategyText = (campaign, i18n) => {
  if (campaign.CampaignType === 'PerformanceMax') {
    return campaign?.BiddingScheme?.Type === MAX_CONVERSIONS.type ? i18n.getString(MAX_CONVERSIONS.text) : i18n.getString(MAX_CONVERSIONS_VALUE.text);
  }
  if (campaign.CampaignType === 'LinkedIn') {
    return getLinkedInBidStrategyText(campaign, i18n);
  }
  return '';
};

export const BidStrategyCard: React.FunctionComponent<{ contentReady: Function }> = ({ contentReady }: { contentReady: Function }) => {
  const i18n = useContext(I18nContext) as any;
  const instrumentation = useContext(InstrumentationContext) as any;

  const [bidStrategyText, setBidStrategyText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const log = useMemo(() => {
    if (instrumentation && instrumentation.campaignOverviewEdit) {
      const scenario = instrumentation.campaignOverviewEdit.create();
      return scenario.bidStrategyEdit.create();
    }
    return {
      trace: _.noop,
      error: _.noop,
    };
  }, [instrumentation]);

  const set = useCallback((campaign) => {
    setBidStrategyText(getBidStrategyText(campaign, i18n));
    setIsLoading(false);
    contentReady();
  }, [contentReady, i18n]);
  const readCampaign = useReadCampaign({ set });
  useEffect(() => {
    setIsLoading(true);
    readCampaign();
  }, [i18n, readCampaign, contentReady]);

  useEffect(() => {
    log.trace('Bid strategy card rendered');
  }, [log]);

  const ChildComponent = useMemo(() => (props => <BidStrategyForm {...props} log={log} />), [log]);

  return (
    <StatusSummaryCard
      header={i18n.getString(_TL_('Bid Strategy'))}
      description={bidStrategyText}
      IconComponent={Gavel20Regular}
      color="brown"
      isLoading={isLoading}
      dialogEditProps={{
        title: i18n.getString(_TL_('Set bidding strategy')),
        ChildComponent,
        buttonAriaLabel: i18n.getString(_TL_('Edit bid strategy')),
      }}
      className="bid-strategy-edit-card"
    />
  );
};
